import React, { useMemo } from 'react';
import Badge from '../../components/ui/Badge';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import {
  ReferenceDataGeneralList,
  type ReferenceInboxColumn,
} from '../parameters/ReferenceDataGeneralList';
import { moduleConfig } from './config';
import { useVesselOperationTypes, type VesselOperationType } from './useData';

const List: React.FC = () => {
  const { colors } = useTheme();
  const formatDate = moduleConfig.utils.formatDate;

  const columns = useMemo<ReferenceInboxColumn<VesselOperationType>[]>(
    () => [
      {
        key: 'name',
        header: 'Description',
        render: (row) => <span className={cn('font-medium', colors.text)}>{row.name}</span>,
      },
      {
        key: 'color',
        header: 'Color',
        sortable: false,
        className: 'whitespace-nowrap',
        render: (row) => (
          <span className="inline-flex items-center gap-2">
            <span
              className="inline-block h-5 w-10 rounded border border-black/10 dark:border-white/10"
              style={{ backgroundColor: row.color }}
              title={row.color}
            />
            <span className={cn('hidden font-mono text-xs sm:inline', colors.textMuted)}>
              {row.color}
            </span>
          </span>
        ),
      },
      {
        key: 'notes',
        header: 'Notes',
        sortable: false,
        className: 'hidden max-w-xs truncate md:table-cell',
        render: (row) => <span className={colors.textMuted}>{row.notes || '—'}</span>,
      },
      {
        key: 'sortOrder',
        header: 'AA',
        className: 'whitespace-nowrap',
        render: (row) => <span className={cn('tabular-nums', colors.textMuted)}>{row.sortOrder}</span>,
      },
      {
        key: 'isActive',
        header: 'Active',
        className: 'whitespace-nowrap',
        render: (row) => (
          <Badge variant={row.isActive ? 'success' : 'danger'}>{row.isActive ? 'Yes' : 'No'}</Badge>
        ),
      },
      {
        key: 'createdAt',
        header: 'Created',
        className: 'hidden whitespace-nowrap lg:table-cell',
        render: (row) => <span className={colors.textMuted}>{formatDate(row.createdAt)}</span>,
      },
    ],
    [colors.text, colors.textMuted, formatDate],
  );

  return (
    <ReferenceDataGeneralList
      moduleConfig={moduleConfig}
      useData={useVesselOperationTypes}
      columns={columns}
      defaultSort={{ key: 'sortOrder', direction: 'asc' }}
      newLabel="New operation type"
      getRowLabel={(row) => row.name}
      loadingLabel="Loading operation types…"
    />
  );
};

export default List;
